import { useState, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { listCdr, getSummary, type CdrParams } from "../api/reports";
import { withFees, FEES_NOTE } from "../lib/fees";

function fmtDuration(sec: number): string {
  if (!sec) return "0:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function SummaryBand() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ["report-summary", days],
    queryFn: () => getSummary(days),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Account Summary</h2>
        <select
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
      {isLoading || !data ? (
        <p className="text-gray-500 text-sm">Loading summary…</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Calls" value={data.total_calls.toLocaleString()} sub={`${data.campaigns_active} campaigns`} />
          <StatCard label="Answer rate" value={`${data.answer_rate_pct}%`} sub={`${data.answered.toLocaleString()} answered`} />
          <StatCard label="Talk time" value={fmtDuration(data.total_billsec)} />
          <StatCard label="Spend" value={`$${withFees(data.total_cost).toFixed(2)}`} sub={FEES_NOTE} />
        </div>
      )}
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

const PAGE_SIZE = 50;

export function ReportsPage() {
  const [filters, setFilters] = useState<CdrParams>({ page: 1, page_size: PAGE_SIZE });
  // Phone search is held locally and debounced into `filters`, so typing a
  // 10-digit number fires one query instead of ten.
  const [phoneInput, setPhoneInput] = useState("");

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((f) => {
        // Under 3 digits the backend ignores the filter anyway (too
        // unselective to search), so don't fire a query for it.
        const digits = phoneInput.replace(/\D/g, "");
        const next = digits.length >= 3 ? phoneInput.trim() : undefined;
        if (f.phone === next) return f; // no-op keeps the query key stable
        return { ...f, phone: next, page: 1 };
      });
    }, 350);
    return () => clearTimeout(id);
  }, [phoneInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["cdr", filters],
    queryFn: () => listCdr(filters),
    placeholderData: keepPreviousData,
  });

  const page = filters.page ?? 1;
  const totalPages = data?.pages ?? 1;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Reports</h1>

      <SummaryBand />

      <h2 className="text-lg font-semibold mb-2">Call Detail Records</h2>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative">
          <input
            type="search"
            inputMode="tel"
            value={phoneInput}
            placeholder="Search phone number…"
            aria-label="Search by phone number"
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-56"
            onChange={(e) => setPhoneInput(e.target.value)}
          />
          {phoneInput && (
            <button
              type="button"
              aria-label="Clear phone search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              onClick={() => setPhoneInput("")}
            >
              ×
            </button>
          )}
        </div>
        <input
          type="date"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          onChange={(e) =>
            setFilters((f) => ({ ...f, date_from: e.target.value || undefined, page: 1 }))
          }
        />
        <input
          type="date"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          onChange={(e) =>
            setFilters((f) => ({ ...f, date_to: e.target.value || undefined, page: 1 }))
          }
        />
        <select
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          onChange={(e) =>
            setFilters((f) => ({ ...f, disposition: e.target.value || undefined, page: 1 }))
          }
        >
          <option value="">Any disposition</option>
          <option value="ANSWERED">Answered</option>
          <option value="BUSY">Busy</option>
          <option value="NO ANSWER">No answer</option>
          <option value="FAILED">Failed</option>
        </select>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} records`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Time</th>
                <th className="px-4 py-2">Campaign</th>
                <th className="px-4 py-2">Phone</th>
                <th className="px-4 py-2">Disposition</th>
                <th className="px-4 py-2 text-right">Duration</th>
                <th className="px-4 py-2 text-right">Cost (incl. fees)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-gray-500">
                    No call records for these filters.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((r) => (
                  <tr key={r.cdr_id}>
                    <td className="px-4 py-2 text-gray-600">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2">{r.campaign_name}</td>
                    <td className="px-4 py-2">{r.phone}</td>
                    <td className="px-4 py-2">{r.disposition}</td>
                    <td className="px-4 py-2 text-right">{fmtDuration(r.billsec)}</td>
                    <td className="px-4 py-2 text-right">${withFees(r.cost).toFixed(4)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: page - 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: page + 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
